"use client";

import { useEffect, useRef } from "react";
import { cn } from "@repo/lib";
import { env } from "#/utilities/env";

export function Grid(props: React.ComponentProps<"ul">) {
  // 1. Create a ref to access the actual DOM node
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const container = navRef.current;

    // Safety check: ensure the element exists and we are in a browser environment
    if (!container || typeof window === "undefined") return;

    // 2. Logic moved inside useEffect so it runs after the DOM is ready
    if ("onscrollsnapchange" in window) {
      container.classList.add("snapevents");

      // Access the first DOM child safely
      if (container.children[0]) {
        container.children[0].classList.add("seen");
      }

      // 3. Define the event handler
      const handleSnapChange = (event: SnapEvent) => {
        // Note: 'snapTargetInline' is experimental; ensure browser support or polyfills
        const target = event.snapTargetInline || event.snapTargetBlock;

        container.querySelector(".seen")?.classList.remove("seen");

        if (target) {
          target.classList.add("seen");
        }
      };

      // 4. Attach the listener to the container (the ref), not props.children
      container.addEventListener("scrollsnapchanging", handleSnapChange);

      // 5. Cleanup function to remove listener when component unmounts
      return () => {
        container.removeEventListener("scrollsnapchanging", handleSnapChange);
      };
    }
  }, []); // Empty dependency array means this runs once on mount

  return (
    <nav
      {...props}
      ref={navRef} // Attach the ref here
      className={cn(
        "grid grid-flow-row gap-2 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 4xl:grid-cols-5 6xl:grid-cols-6 list-none",
        {
          "xl:grid-cols-3":
            env.NEXT_PUBLIC_FEATURE_FLAG_SEARCH_COLLECTIONS_ENABLE === true,
        },
        props.className,
      )}
    >
      {props.children}
    </nav>
  );
}
